import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search, Filter, ChevronDown, Tag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ProductCard from "@/components/store/ProductCard";
import RecentlyViewed from "@/components/store/RecentlyViewed";
import SheinFeaturedLooks from "@/components/store/SheinFeaturedLooks";
import CartDrawer from "@/components/store/CartDrawer";
import StickyCartButton from "@/components/store/StickyCartButton";
import { useLanguage } from "@/context/LanguageContext";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import { useStock } from "@/context/StockContext";
import {
  ALL_PRODUCTS,
  SHEIN_SUBCATEGORIES,
  computeDiscountedPrice,
  getProductDiscountPct,
  isAvailable,
  localized,
} from "@/data/products";
import { trackEvent } from "@/lib/analytics";

const FILTERS_KEY = "bob_shein_filters";
const RETURN_KEY = "bob_store_return";
const RETURN_MAX_AGE_MS = 30 * 60 * 1000;

const readStoredFilters = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const peekReturnMarker = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    if (Date.now() - (parsed.ts || 0) > RETURN_MAX_AGE_MS) {
      sessionStorage.removeItem(RETURN_KEY);
      return null;
    }
    if (parsed.origin && parsed.origin !== "/stock/shein") return null;
    return parsed;
  } catch {
    return null;
  }
};

const SHEIN_PRODUCTS_ALL = ALL_PRODUCTS.filter((p) => p.category === "shein");

export default function SheinPage() {
  const { t, lang, isRTL } = useLanguage();
  const S = t.store;
  const SH = t.shein;

  const stored = readStoredFilters();
  const [subcat, setSubcat] = useState(stored?.subcat || "all");
  const [search, setSearch] = useState(stored?.search || "");
  const [availableOnly, setAvailableOnly] = useState(stored?.availableOnly || false);
  const [sortBy, setSortBy] = useState(stored?.sortBy || "default");
  const [highlightId, setHighlightId] = useState(null);
  const previousSearchRef = useRef(search);
  const previousSubcatRef = useRef(subcat);
  const previousAvailableOnlyRef = useRef(availableOnly);
  const previousSortRef = useRef(sortBy);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = `${SH.heroTitle} - BOB Services LB`;
    }
  }, [SH.heroTitle]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        FILTERS_KEY,
        JSON.stringify({ subcat, search, availableOnly, sortBy })
      );
    } catch {
      // ignore
    }
  }, [subcat, search, availableOnly, sortBy]);

  const { version: catalogVersion, sheinCategories: remoteCategories } = useCatalog();
  const { stockMap, loaded: stockLoaded } = useStock();
  const productAvailable = (p) => isAvailable(p, stockLoaded ? stockMap[p.id] : undefined);
  const SHEIN_CATEGORIES = useMemo(() => {
    if (Array.isArray(remoteCategories) && remoteCategories.length > 0) {
      return remoteCategories.map((c) => ({ id: c.id, label: c.name || c.label || {} }));
    }
    return SHEIN_SUBCATEGORIES;
  }, [remoteCategories]);

  const filtered = useMemo(() => {
    let list = SHEIN_PRODUCTS_ALL.filter((p) => !isHiddenById(p.id));
    if (subcat !== "all") list = list.filter((p) => p.sheinCategory === subcat);
    if (availableOnly) list = list.filter((p) => productAvailable(p));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const idStr = `#${p.id}`.toLowerCase();
        const name = localized(p.name, lang).toLowerCase();
        return (
          idStr.includes(q) ||
          p.id.includes(q.replace(/[#]/g, "")) ||
          name.includes(q)
        );
      });
    }
    if (sortBy === "priceLow" || sortBy === "priceHigh") {
      const dir = sortBy === "priceLow" ? 1 : -1;
      list.sort((a, b) => {
        const ap = computeDiscountedPrice(a.originalPrice, getProductDiscountPct(a));
        const bp = computeDiscountedPrice(b.originalPrice, getProductDiscountPct(b));
        if (ap == null && bp == null) return 0;
        if (ap == null) return 1;
        if (bp == null) return -1;
        return (ap - bp) * dir;
      });
    }
    return list;
  }, [subcat, availableOnly, search, sortBy, lang, catalogVersion, stockLoaded, stockMap]);

  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackEvent("shein_view", {
      routeName: "shein",
      metadata: {
        visible_products: SHEIN_PRODUCTS_ALL.length,
      },
    });
  }, []);

  const countsByCat = useMemo(() => {
    const counts = { all: SHEIN_PRODUCTS_ALL.length };
    for (const sc of SHEIN_CATEGORIES) {
      counts[sc.id] = SHEIN_PRODUCTS_ALL.filter((p) => p.sheinCategory === sc.id).length;
    }
    return counts;
  }, [SHEIN_CATEGORIES]);

  useEffect(() => {
    const trimmed = search.trim();
    if (previousSearchRef.current === search) return undefined;
    previousSearchRef.current = search;
    if (!trimmed) return undefined;
    const timer = window.setTimeout(() => {
      trackEvent("product_search", {
        routeName: "shein",
        searchQuery: trimmed,
        metadata: { scope: "shein", results_count: filtered.length },
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [filtered.length, search]);

  useEffect(() => {
    if (previousSubcatRef.current === subcat) return;
    previousSubcatRef.current = subcat;
    trackEvent("product_filter", {
      routeName: "shein",
      metadata: { scope: "shein", filter: "subcategory", value: subcat, results_count: filtered.length },
    });
  }, [filtered.length, subcat]);

  useEffect(() => {
    if (previousAvailableOnlyRef.current === availableOnly) return;
    previousAvailableOnlyRef.current = availableOnly;
    trackEvent("product_filter", {
      routeName: "shein",
      metadata: {
        scope: "shein",
        filter: "available_only",
        value: availableOnly ? "on" : "off",
        results_count: filtered.length,
      },
    });
  }, [availableOnly, filtered.length]);

  useEffect(() => {
    if (previousSortRef.current === sortBy) return;
    previousSortRef.current = sortBy;
    trackEvent("product_sort", {
      routeName: "shein",
      metadata: { scope: "shein", sort: sortBy, results_count: filtered.length },
    });
  }, [filtered.length, sortBy]);

  useEffect(() => {
    const marker = peekReturnMarker();
    if (!marker) return;
    let raf1 = 0;
    let raf2 = 0;
    let timer = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        let scrolled = false;
        if (marker.productId) {
          const containerSelector =
            marker.source === "recent"
              ? '[data-testid="recently-viewed-track"]'
              : '[data-testid="shein-grid"]';
          const container = document.querySelector(containerSelector);
          const sel = `[data-testid="product-card-${marker.productId}"]`;
          const target =
            (container && container.querySelector(sel)) ||
            document.querySelector(`[data-testid="shein-grid"] ${sel}`);
          if (target) {
            const rect = target.getBoundingClientRect();
            const offset = window.innerWidth < 768 ? 90 : 110;
            const y = (window.scrollY || window.pageYOffset || 0) + rect.top - offset;
            window.scrollTo({ top: Math.max(0, y), behavior: "instant" });
            setHighlightId(marker.productId);
            timer = window.setTimeout(() => setHighlightId(null), 1800);
            scrolled = true;
          }
        }
        if (!scrolled && typeof marker.scrollY === "number") {
          window.scrollTo({ top: marker.scrollY, behavior: "instant" });
        }
        try {
          sessionStorage.removeItem(RETURN_KEY);
        } catch {
          // ignore
        }
      });
    });
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (timer) clearTimeout(timer);
    };
  }, [filtered]);

  return (
    <main
      className="min-h-screen overflow-x-hidden bg-[#FAFAFA] text-[#111] dark:bg-[#080808] dark:text-[#F6F7FB]"
      data-testid="shein-page"
    >
      <Navbar />

      <section
        className="relative overflow-hidden px-5 pb-12 pt-24 md:px-8 md:pb-16 md:pt-28"
        data-testid="shein-hero"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#FFEEF4] via-[#FFF7FB] to-[#FAFAFA] dark:from-[#080808] dark:via-[#101010] dark:to-[#080808]" />
        <div className="pointer-events-none absolute -top-24 -end-24 h-[28rem] w-[28rem] rounded-full bg-[#F5B3CB]/30 blur-[80px]" />
        <div className="pointer-events-none absolute -start-32 top-40 h-[22rem] w-[22rem] rounded-full bg-[#F37021]/15 blur-[100px]" />

        <div className="relative mx-auto max-w-7xl">
          <div className="mb-6">
            <Link
              to="/stock"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-black/10 bg-white/85 px-4 text-sm font-medium text-[#111] shadow-[0_10px_24px_rgba(16,24,40,0.06)] backdrop-blur transition-all hover:-translate-y-0.5 hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-white/10 dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
              data-testid="shein-back-to-store"
            >
              <ArrowLeft className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
              {SH.backToBobStore}
            </Link>
          </div>

          <div className="rounded-[32px] border border-[#F2CEDC] bg-white/72 px-6 py-8 shadow-[0_26px_70px_rgba(245,131,178,0.12)] backdrop-blur-sm md:px-10 md:py-10 dark:border-white/10 dark:bg-[#121212]/82">
            <div className="flex flex-col items-center text-center">
              <img
                src="/shein-logo.jpg"
                alt="SHEIN"
                className="h-20 w-20 rounded-full object-cover ring-4 ring-white shadow-[0_12px_36px_rgba(245,131,178,0.28)] md:h-24 md:w-24"
                data-testid="shein-logo"
              />

              <span
                className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#111] px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-white shadow-[0_10px_24px_rgba(17,17,17,0.16)]"
                data-testid="shein-discount-chip"
              >
                <Tag className="h-3.5 w-3.5" strokeWidth={2.5} />
                {SH.discountChip}
              </span>

              <h1
                className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[#111] sm:text-5xl lg:text-6xl dark:text-white"
                data-testid="shein-title"
              >
                {SH.heroTitle}
              </h1>
              <p
                className="mt-4 max-w-2xl text-base leading-relaxed text-[#444] sm:text-lg dark:text-[#BAC4D4]"
                data-testid="shein-subtitle"
              >
                {SH.heroSubtitle}
              </p>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-black/6 bg-white px-3.5 py-2 text-xs font-medium text-[#444] shadow-sm sm:text-sm dark:border-white/10 dark:bg-white/10 dark:text-[#DDE5F2]">
                  <span className="h-2 w-2 rounded-full bg-[#F37021]" />
                  {SH.featureFastShip}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-black/6 bg-white px-3.5 py-2 text-xs font-medium text-[#444] shadow-sm sm:text-sm dark:border-white/10 dark:bg-white/10 dark:text-[#DDE5F2]">
                  <span className="h-2 w-2 rounded-full bg-[#F37021]" />
                  {SH.featureSecure}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-black/6 bg-white px-3.5 py-2 text-xs font-medium text-[#444] shadow-sm sm:text-sm dark:border-white/10 dark:bg-white/10 dark:text-[#DDE5F2]">
                  <span className="h-2 w-2 rounded-full bg-[#F37021]" />
                  {SH.featureCount.replace("{count}", String(SHEIN_PRODUCTS_ALL.length))}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 md:mt-8">
            <SheinFeaturedLooks />
          </div>
        </div>
      </section>

      <RecentlyViewed />

      <section className="px-5 pt-6 md:px-8 md:pt-10" data-testid="shein-filters-section">
        <div className="mx-auto max-w-7xl rounded-[30px] border border-black/6 bg-white p-4 shadow-[0_18px_45px_rgba(16,24,40,0.05)] md:p-5 dark:border-white/10 dark:bg-[#121212]">
          <div className="relative mb-5">
            <Search className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#888] dark:text-[#8FA1B8]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={SH.searchPlaceholder}
              className="h-12 w-full rounded-full border border-black/10 bg-[#FAFAFA] pe-4 ps-11 text-sm text-[#111] placeholder:text-[#999] transition-colors focus:border-[#F37021] focus:outline-none dark:border-white/10 dark:bg-[#121212] dark:text-white"
              data-testid="shein-search-input"
              aria-label={SH.searchPlaceholder}
            />
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div
              className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0"
              data-testid="shein-subcat-pills"
            >
              <SubcatPill
                active={subcat === "all"}
                onClick={() => setSubcat("all")}
                testId="shein-cat-all"
                count={countsByCat.all}
              >
                {SH.subcatAll}
              </SubcatPill>
              {SHEIN_CATEGORIES.map((sc) => {
                const n = countsByCat[sc.id] || 0;
                if (n === 0) return null;
                return (
                  <SubcatPill
                    key={sc.id}
                    active={subcat === sc.id}
                    onClick={() => setSubcat(sc.id)}
                    testId={`shein-cat-${sc.id}`}
                    count={n}
                  >
                    {localized(sc.label, lang)}
                  </SubcatPill>
                );
              })}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={() => setAvailableOnly((v) => !v)}
                className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-all sm:w-auto ${
                  availableOnly
                    ? "border border-[#111] bg-[#111] text-white shadow-[0_12px_26px_rgba(17,17,17,0.12)]"
                    : "border border-black/10 bg-[#FAFAFA] text-[#111] hover:border-[#111] hover:bg-white dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B]"
                }`}
                data-testid="shein-toggle-available"
              >
                <Filter className="h-4 w-4" />
                {S.availableOnly}
              </button>

              <div className="relative w-full sm:w-auto">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-10 w-full cursor-pointer appearance-none rounded-full border border-black/10 bg-[#FAFAFA] pe-9 ps-4 text-sm font-medium text-[#111] transition-colors hover:border-[#111] hover:bg-white focus:border-[#F37021] focus:outline-none sm:min-w-[180px] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B]"
                  data-testid="shein-sort-select"
                  aria-label={S.sortBy}
                >
                  <option value="default">{S.sortDefault}</option>
                  <option value="priceLow">{S.sortPriceLow}</option>
                  <option value="priceHigh">{S.sortPriceHigh}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666] dark:text-[#8FA1B8]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="px-5 pb-20 pt-8 md:px-8 md:pb-28 md:pt-12"
        data-testid="shein-grid-section"
      >
        <div className="mx-auto max-w-7xl">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#111] sm:text-[32px] dark:text-white">
                {SH.heroTitle}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center rounded-full bg-[#111] px-3 py-1.5 text-sm font-medium text-white">
                  {filtered.length}
                </span>
                {search.trim() ? (
                  <span className="inline-flex items-center rounded-full border border-[#F5B3CB]/45 bg-[#FFF4F8] px-4 py-1.5 text-sm text-[#B85D86] dark:bg-[#1A1A1A] dark:text-[#F2A2C4]">
                    "{search.trim()}"
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div
              className="rounded-[30px] border border-dashed border-[#E9C9D8] bg-white px-6 py-20 text-center text-[#666] shadow-[0_18px_45px_rgba(16,24,40,0.04)] dark:border-white/10 dark:bg-[#121212] dark:text-[#BAC4D4]"
              data-testid="shein-empty-state"
            >
              <p className="text-lg font-medium">{S.noResults}</p>
              <p className="mt-2 text-sm">{S.noResultsHint}</p>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.04 } },
              }}
              className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-2 sm:gap-x-5 md:grid-cols-3 md:gap-x-6 md:gap-y-10 lg:grid-cols-4"
              data-testid="shein-grid"
            >
              {filtered.map((p) => (
                <motion.div
                  key={p.id}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                  }}
                >
                  <ProductCard product={p} highlight={highlightId === p.id} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <CartDrawer />
      <StickyCartButton />
    </main>
  );
}

const SubcatPill = ({ active, onClick, children, testId, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium transition-all ${
      active
        ? "border border-[#111] bg-[#111] text-white shadow-[0_12px_26px_rgba(17,17,17,0.12)]"
        : "border border-black/10 bg-[#FAFAFA] text-[#111] hover:border-[#F37021] hover:bg-white hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]"
    }`}
    data-testid={testId}
  >
    <span>{children}</span>
    {typeof count === "number" && (
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${
          active ? "bg-white/15 text-white" : "bg-[#FFF1E7] text-[#F37021] dark:bg-[#1A1A1A] dark:text-[#F8A056]"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);
